import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { LoginBackground } from "@/components/layout/LoginBackground";
import { SignUpWelcome } from "@/components/auth/SignUpWelcome";
import { SignInWelcome } from "@/components/auth/SignInWelcome";

const Login = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<'sign_up' | 'sign_in'>('sign_up');

  useEffect(() => {
    // Clear any existing auth data on mount
    localStorage.removeItem('supabase.auth.token');
    sessionStorage.removeItem('supabase.auth.token');

    // Check if user is already logged in
    const checkUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Session check error:', error);
          return;
        }
        
        if (session) {
          console.log('Active session found, redirecting to app');
          navigate("/app");
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };

    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed in Login:', event, !!session);
      
      if (event === 'SIGNED_IN' && session) {
        // Ensure we have a valid session before redirecting
        try {
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError) {
            console.error('User verification failed:', userError);
            return;
          }
          
          if (user) {
            console.log('Valid user found, redirecting to app');
            navigate("/app");
          }
        } catch (error) {
          console.error('Error verifying user:', error);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const renderContent = () => {
    if (view === 'sign_up') {
      return (
        <div className="space-y-6">
          <SignUpWelcome />

          <Auth
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: 'rgb(var(--primary))',
                    brandAccent: 'rgb(var(--primary))',
                  },
                },
              },
              className: {
                anchor: 'text-primary hover:text-primary/80',
                button: 'bg-primary hover:bg-primary/80',
                container: 'space-y-4',
                label: 'text-foreground',
                input: 'bg-background',
              },
            }}
            localization={{
              variables: {
                sign_up: {
                  email_label: 'Email',
                  password_label: 'Create Password',
                  button_label: 'Create Account',
                  social_provider_text: 'Sign in with {{provider}}',
                  link_text: 'Already have an account? Sign in',
                },
              },
            }}
            theme="light"
            providers={[]}
            view="sign_up"
          />

          <div className="text-center text-sm text-muted-foreground">
            By creating an account, you agree to our{' '}
            <Link to="/terms-of-service" className="text-primary hover:underline">
              Terms of Service
            </Link>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <Link to="/privacy-policy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <SignInWelcome />

        <Auth
          supabaseClient={supabase}
          appearance={{
            theme: ThemeSupa,
            variables: {
              default: {
                colors: {
                  brand: 'rgb(var(--primary))',
                  brandAccent: 'rgb(var(--primary))',
                },
              },
            },
            className: {
              anchor: 'text-primary hover:text-primary/80',
              button: 'bg-primary hover:bg-primary/80',
              container: 'space-y-4',
              label: 'text-foreground',
              input: 'bg-background',
            },
          }}
          localization={{
            variables: {
              sign_in: {
                email_label: 'Email',
                password_label: 'Password',
                button_label: 'Sign In',
                social_provider_text: 'Sign in with {{provider}}',
                link_text: "Don't have an account? Sign up",
              },
            },
          }}
          theme="light"
          providers={[]}
          view="sign_in"
        />

        <div className="text-center text-sm text-muted-foreground">
          <Link to="/privacy-policy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center p-4 relative overflow-hidden">
      <LoginBackground />
      <Card className="p-8 max-w-md w-full space-y-6 relative z-10">
        {renderContent()}
      </Card>
    </div>
  );
};

export default Login;